import React, { useState } from "react";
import * as XLSX from "xlsx";
import { toast } from "react-toastify";
import { post } from "../../services/api";
import { ArrowLeft, Upload, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const BulkSizeUpdate = () => {
  const [data, setData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);

  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setLoading(true);
    const reader = new FileReader();
    reader.onload = async (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const jsonData = XLSX.utils.sheet_to_json(ws, { header: 1 });

        if (jsonData.length < 2) {
          toast.error("The sheet appears to be empty or missing headers");
          setLoading(false);
          return;
        }

        const rawHeaders = jsonData[0];
        const rows = jsonData.slice(1);

        // Map rows to objects based on headers
        const formattedData = rows.map((row) => {
          const obj = {};
          rawHeaders.forEach((header, index) => {
            obj[header] = row[index];
          });
          return obj;
        }).filter(row => row["Sale Receipt No"]);

        setHeaders(rawHeaders);
        setData(formattedData);
        setResults({ preview: [] }); // Initialize empty preview

        // After parsing, fetch DB comparison in chunks
        await fetchPreview(formattedData, rawHeaders);
      } catch (error) {
        console.error("Error parsing Excel:", error);
        toast.error("Failed to parse Excel file");
      } finally {
        setLoading(false);
      }
    };
    reader.readAsBinaryString(file);
  };

  const fetchPreview = async (formattedData, rawHeaders) => {
    try {
      const groupedMap = new Map();
      const metadataHeaders = ['s.no', 'sale receipt no', 'sale date', 'student name', 'admission no', 'gender', 'class', 'house', 'key', 'product', 'size', 'quantity sold'];

      formattedData.forEach((row) => {
        const orderId = row["Sale Receipt No"];
        const admissionNo = row["Admission No"];
        if (!orderId || !admissionNo) return;

        const key = `${orderId}-${admissionNo}`;
        if (!groupedMap.has(key)) {
          groupedMap.set(key, { orderId, admissionNo, products: [] });
        }
        const group = groupedMap.get(key);

        // 1. Flat format: "Product" and "Size" columns
        if (row["Product"] && row["Size"] !== undefined) {
          group.products.push({ 
            productName: row["Product"], 
            newSize: row["Size"].toString() 
          });
        } else {
          // 2. Matrix or Old Format: Scan all headers
          rawHeaders.forEach((header, index) => {
            if (!header || typeof header !== 'string') return;
            const hLower = header.toLowerCase();
            if (metadataHeaders.includes(hLower)) return;

            const newSize = row[header];
            if (newSize === undefined || newSize === null || newSize.toString().trim() === "") return;

            if (hLower.endsWith("size")) {
              // Old Format logic: Previous column usually contains the product name
              // or the header itself is "Product Name Size"
              const prevHeader = rawHeaders[index - 1];
              const productName = (prevHeader && !metadataHeaders.includes(prevHeader.toLowerCase())) 
                ? row[prevHeader] 
                : header.replace(/ size$/i, "");
              
              if (productName) {
                group.products.push({ productName, newSize: newSize.toString() });
              }
            } else {
              // Matrix format: Header IS the product name
              group.products.push({ productName: header, newSize: newSize.toString() });
            }
          });
        }
      });

      const updates = Array.from(groupedMap.values()).filter(u => u.products.length > 0);

      if (updates.length > 0) {
        setProgress({ current: 0, total: updates.length });
        
        const chunkSize = 50;
        for (let i = 0; i < updates.length; i += chunkSize) {
          const chunk = updates.slice(i, i + chunkSize);
          const res = await post("/bulk-operations/preview-sizes", { rows: chunk });
          
          setResults(prev => ({
            ...prev,
            preview: [...prev.preview, ...res.data]
          }));
          
          setProgress(prev => ({ ...prev, current: Math.min(prev.current + chunkSize, updates.length) }));
        }
      }
    } catch (error) {
      console.error("Error fetching preview:", error);
      toast.error("Failed to fetch database comparison");
    }
  };

  const handleSingleUpdate = async (row, match, rowIndex, matchIndex) => {
    setProcessing(true);
    try {
      const updateData = {
        rows: [{
          orderId: row.orderId,
          admissionNo: row.admissionNo,
          products: [{
            productName: match.productName,
            newSize: match.excelSize.toString()
          }]
        }]
      };

      const res = await post("/bulk-operations/execute-sizes", updateData);
      
      if (res.data.successCount > 0) {
        toast.success(`Updated size for ${match.productName}`);
        
        // Update local state to reflect the change
        const updatedPreview = [...results.preview];
        updatedPreview[rowIndex].matches[matchIndex].dbSize = match.excelSize;
        updatedPreview[rowIndex].matches[matchIndex].status = "Updated Successfully";
        setResults({ ...results, preview: updatedPreview });
      } else {
        toast.error(res.data.details[0]?.message || "Update failed");
      }
    } catch (error) {
      console.error("Single update error:", error);
      toast.error("Failed to update size");
    } finally {
      setProcessing(false);
    }
  };

  const processUpdates = async () => {
    if (!results?.preview) return;

    const updates = results.preview
      .filter(row => row.found)
      .map(row => ({
        orderId: row.orderId,
        admissionNo: row.admissionNo,
        products: row.matches
          .filter(m => m.status === 'Available' && m.excelSize.toString() !== m.dbSize?.toString())
          .map(m => ({
            productName: m.productName,
            newSize: m.excelSize.toString()
          }))
      }))
      .filter(u => u.products.length > 0);

    if (updates.length === 0) {
      toast.info("No size mismatches found to update.");
      return;
    }

    setProcessing(true);
    try {
      const res = await post("/bulk-operations/execute-sizes", { rows: updates });
      
      // Update local state to reflect successful updates
      const updatedPreview = [...results.preview];
      updates.forEach(updateGroup => {
        const rowIdx = updatedPreview.findIndex(r => r.orderId === updateGroup.orderId && r.admissionNo === updateGroup.admissionNo);
        if (rowIdx !== -1) {
          updateGroup.products.forEach(updProd => {
            const matchIdx = updatedPreview[rowIdx].matches.findIndex(m => m.productName === updProd.productName);
            if (matchIdx !== -1) {
              updatedPreview[rowIdx].matches[matchIdx].dbSize = updProd.newSize;
              updatedPreview[rowIdx].matches[matchIdx].status = "Updated Successfully";
            }
          });
        }
      });

      setResults({ ...results, preview: updatedPreview, final: res.data });
      toast.success(res.data.message);
    } catch (error) {
      console.error("Error updating sizes:", error);
      toast.error(error.response?.data?.message || "Failed to update sizes");
    } finally {
      setProcessing(false);
    }
  };

  const scrollToStatus = (statusType) => {
    let targetIdx = -1;
    if (statusType === 'missing-student') {
      targetIdx = results.preview.findIndex(row => !row.found);
    } else if (statusType === 'pending-updates') {
      targetIdx = results.preview.findIndex(row => 
        row.matches.some(m => m.status === 'Available' && m.excelSize.toString() !== m.dbSize?.toString())
      );
    } else if (statusType === 'missing-product') {
      targetIdx = results.preview.findIndex(row => 
        row.matches.some(m => m.status === 'Product Missing in Order')
      );
    }

    if (targetIdx !== -1) {
      const element = document.getElementById(`student-card-${targetIdx}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        element.style.transition = 'all 0.5s ease';
        element.style.ring = '4px solid #0dcaf0';
        setTimeout(() => { element.style.ring = 'none'; }, 2000);
      }
    } else {
      toast.info("No records found for this status");
    }
  };

  return (
    <div className="container-fluid mt-4 pb-5">
      <div className="d-flex align-items-center mb-4">
        <Link to="/admin/management" className="btn btn-outline-secondary me-3">
          <ArrowLeft size={18} />
        </Link>
        <h2 className="mb-0">Bulk Size Update</h2>
      </div>

      <div className="card shadow-sm mb-4">
        <div className="card-body">
          <div className="row align-items-center">
            <div className="col-md-6">
              <label className="form-label fw-bold">Upload Excel Sheet</label>
              <div className="input-group">
                <input
                  type="file"
                  className="form-control"
                  accept=".xlsx, .xls"
                  onChange={handleFileUpload}
                  disabled={loading || processing}
                />
                <span className="input-group-text">
                  <Upload size={18} />
                </span>
              </div>
              <small className="text-muted mt-2 d-block">
                Expected format: "Sale Receipt No", "Admission No", followed by Product names and "New [Product] Size" columns.
              </small>
            </div>
            <div className="col-md-6 text-md-end mt-3 mt-md-0">
              {data.length > 0 && (
                <button
                  className="btn btn-primary px-4 py-2"
                  onClick={processUpdates}
                  disabled={processing}
                >
                  {processing ? (
                    <>
                      <Loader2 className="spinner-border spinner-border-sm me-2" />
                      Updating...
                    </>
                  ) : (
                    "Process Size Updates"
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="card shadow-sm mb-4 border-primary">
          <div className="card-body text-center py-4">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <h5 className="text-primary mb-2">Analyzing data and checking database...</h5>
            {progress.total > 0 && (
              <div className="col-md-6 mx-auto mt-3">
                <div className="progress" style={{ height: "20px" }}>
                  <div 
                    className="progress-bar progress-bar-striped progress-bar-animated" 
                    role="progressbar" 
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                    aria-valuenow={(progress.current / progress.total) * 100} 
                    aria-valuemin="0" 
                    aria-valuemax="100"
                  >
                    {Math.round((progress.current / progress.total) * 100)}%
                  </div>
                </div>
                <p className="mt-2 text-muted small">Verified {progress.current} of {progress.total} records</p>
              </div>
            )}
          </div>
        </div>
      )}

      {results?.final && (
        <div className={`alert ${results.final.success ? 'alert-success' : 'alert-warning'} shadow-sm mb-4`}>
          <div className="d-flex align-items-center">
            {results.final.success ? <CheckCircle className="me-2" /> : <AlertCircle className="me-2" />}
            <div>
              <h5 className="alert-heading mb-1">{results.final.message}</h5>
              <p className="mb-0">
                Successful: <strong>{results.final.successCount}</strong> | 
                Failed: <strong>{results.final.failedCount}</strong>
              </p>
            </div>
          </div>
        </div>
      )}

      {results?.preview && (
        <div className="card shadow-sm mb-4 border-info">
          <div className="card-header bg-info text-white d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Database Verification Preview</h5>
            <span className="badge bg-white text-info">{results.preview.length} Students Grouped</span>
          </div>
          
          {/* Summary Stats Bar */}
          <div className="bg-light border-bottom p-3">
            <div className="row g-2">
              <div className="col-md-2">
                <div className="d-flex align-items-center bg-white p-2 rounded border shadow-sm" style={{ minHeight: '65px' }}>
                  <div className="bg-info bg-opacity-10 p-2 rounded me-2">
                    <Loader2 size={18} className="text-info" />
                  </div>
                  <div>
                    <div className="small text-muted text-uppercase fw-bold" style={{ fontSize: '0.6rem' }}>Total Students</div>
                    <div className="h6 mb-0 fw-bold">{results.preview.length}</div>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="d-flex align-items-center bg-white p-2 rounded border shadow-sm" style={{ minHeight: '65px' }}>
                  <div className="bg-primary bg-opacity-10 p-2 rounded me-2">
                    <CheckCircle size={18} className="text-primary" />
                  </div>
                  <div>
                    <div className="small text-muted text-uppercase fw-bold" style={{ fontSize: '0.6rem' }}>Total Products</div>
                    <div className="h6 mb-0 fw-bold">
                      {results.preview.reduce((acc, row) => acc + row.matches.length, 0)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div className="d-flex align-items-center bg-white p-2 rounded border shadow-sm" style={{ minHeight: '65px' }}>
                  <div className="bg-success bg-opacity-10 p-2 rounded me-2">
                    <CheckCircle size={18} className="text-success" />
                  </div>
                  <div>
                    <div className="small text-muted text-uppercase fw-bold" style={{ fontSize: '0.6rem' }}>Matches</div>
                    <div className="h6 mb-0 fw-bold text-success">
                      {results.preview.reduce((acc, row) => 
                        acc + row.matches.filter(m => m.status === 'Available' && m.excelSize.toString() === m.dbSize?.toString()).length, 0
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div 
                  className="d-flex align-items-center bg-white p-2 rounded border border-warning shadow-sm cursor-pointer" 
                  style={{ minHeight: '65px', cursor: 'pointer', transition: 'transform 0.2s' }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  onClick={() => scrollToStatus('pending-updates')}
                >
                  <div className="bg-warning bg-opacity-10 p-2 rounded me-2">
                    <AlertCircle size={18} className="text-warning" />
                  </div>
                  <div>
                    <div className="small text-muted text-uppercase fw-bold" style={{ fontSize: '0.6rem' }}>Pending Updates</div>
                    <div className="h6 mb-0 fw-bold text-warning">
                      {results.preview.reduce((acc, row) => 
                        acc + row.matches.filter(m => m.status === 'Available' && m.excelSize.toString() !== m.dbSize?.toString()).length, 0
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div 
                  className="d-flex align-items-center bg-white p-2 rounded border border-danger shadow-sm cursor-pointer" 
                  style={{ minHeight: '65px', cursor: 'pointer', transition: 'transform 0.2s' }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  onClick={() => scrollToStatus('missing-student')}
                >
                  <div className="bg-danger bg-opacity-10 p-2 rounded me-2">
                    <AlertCircle size={18} className="text-danger" />
                  </div>
                  <div>
                    <div className="small text-muted text-uppercase fw-bold" style={{ fontSize: '0.6rem' }}>Missing Student</div>
                    <div className="h6 mb-0 fw-bold text-danger">
                      {results.preview.filter(row => !row.found).length}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-2">
                <div 
                  className="d-flex align-items-center bg-white p-2 rounded border border-danger shadow-sm cursor-pointer" 
                  style={{ minHeight: '65px', cursor: 'pointer', transition: 'transform 0.2s' }}
                  onMouseOver={(e) => e.currentTarget.style.transform = 'translateY(-2px)'}
                  onMouseOut={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                  onClick={() => scrollToStatus('missing-product')}
                >
                  <div className="bg-danger bg-opacity-10 p-2 rounded me-2">
                    <AlertCircle size={18} className="text-danger" />
                  </div>
                  <div>
                    <div className="small text-muted text-uppercase fw-bold" style={{ fontSize: '0.6rem' }}>Missing Product</div>
                    <div className="h6 mb-0 fw-bold text-danger">
                      {results.preview.reduce((acc, row) => acc + row.matches.filter(m => m.status === 'Product Missing in Order').length, 0)}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card-body p-3" style={{ maxHeight: "75vh", overflowY: "auto" }}>
            {results.preview.map((row, idx) => {
              const studentName = typeof row.dbData === 'object' ? row.dbData.studentName : row.dbData;
              const db = typeof row.dbData === 'object' ? row.dbData : {};
              
              return (
                <div key={idx} id={`student-card-${idx}`} className={`card mb-4 shadow-sm border ${!row.found ? 'border-danger' : 'border-light'}`}>
                  <div className={`card-header d-flex justify-content-between align-items-center py-2 ${!row.found ? 'bg-danger text-white' : 'bg-light'}`}>
                    <div>
                      <span className="fw-bold h6 mb-0">{studentName}</span>
                      <span className="ms-3 badge bg-dark opacity-75">Receipt: {row.orderId}</span>
                      <span className="ms-2 badge bg-dark opacity-75">Adm: {row.admissionNo}</span>
                      {db.gender && <span className="ms-2 badge bg-secondary">{db.gender}</span>}
                      {db.class && <span className="ms-2 badge bg-info text-dark">Class: {db.class}{db.section ? ` - ${db.section}` : ''}</span>}
                      {db.house && <span className="ms-2 badge bg-primary">House: {db.house}</span>}
                      {db.studentType && <span className="ms-2 badge bg-dark">{db.studentType}</span>}
                    </div>
                    {!row.found && <span className="small fw-bold text-uppercase">Record Not Found</span>}
                  </div>
                  <div className="card-body p-0">
                    <table className="table table-sm table-hover mb-0 align-middle">
                      <thead className="table-light small text-uppercase">
                        <tr>
                          <th className="ps-3" style={{ width: '40%' }}>Product</th>
                          <th className="text-center" style={{ width: '15%' }}>Sheet Size</th>
                          <th className="text-center" style={{ width: '15%' }}>DB Size</th>
                          <th className="text-end pe-3" style={{ width: '30%' }}>Status / Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {row.matches.map((match, midx) => {
                          const hasChange = match.excelSize.toString() !== match.dbSize?.toString();
                          const isAvailable = match.status === 'Available';
                          const isUpdated = match.status === 'Updated Successfully';
                          
                          return (
                            <tr key={midx} className={hasChange && isAvailable ? 'table-warning' : ''}>
                              <td className="ps-3 py-2 small fw-medium">{match.productName}</td>
                              <td className="text-center fw-bold text-primary">{match.excelSize}</td>
                              <td className={`text-center fw-bold ${match.dbSize === 'Not Found' ? 'text-danger' : ''}`}>
                                {match.dbSize}
                              </td>
                              <td className="text-end pe-3">
                                <div className="d-flex align-items-center justify-content-end">
                                  {isAvailable ? (
                                    hasChange ? (
                                      <span className="badge bg-warning text-dark me-2">Size Mismatch</span>
                                    ) : (
                                      <span className="badge bg-success me-2">Match</span>
                                    )
                                  ) : isUpdated ? (
                                    <span className="badge bg-primary me-2">Updated</span>
                                  ) : (
                                    <span className="badge bg-danger me-2">{match.status}</span>
                                  )}

                                  {isAvailable && hasChange && (
                                    <button 
                                      className="btn btn-success btn-xs py-0 px-2"
                                      style={{ fontSize: '0.7rem' }}
                                      onClick={() => handleSingleUpdate(row, match, idx, midx)}
                                      disabled={processing}
                                    >
                                      Update DB
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {data.length > 0 && !loading && (
        <div className="card shadow-sm">
          <div className="card-header bg-light">
            <h5 className="mb-0">Full Sheet Content ({data.length} rows)</h5>
          </div>
          <div className="card-body p-0">
            <div className="table-responsive" style={{ maxHeight: "40vh" }}>
              <table className="table table-hover table-striped mb-0">
                <thead className="table-dark sticky-top">
                  <tr>
                    {headers.map((h, i) => (
                      <th key={i} className="text-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.slice(0, 100).map((row, idx) => (
                    <tr key={idx}>
                      {headers.map((h, i) => (
                        <td key={i}>{row[h]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkSizeUpdate;
